import { useState, useEffect } from 'react';
import { X, FileText, Save } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

/**
 * Modal for editing model-level Business Rules (Markdown).
 * Analogous to BusinessRulesModal but scoped to the model itself.
 */
const ModelBusinessRulesModal = ({ isOpen, onClose, modelName, value, onSave }) => {
  const [text, setText] = useState('');
  const [preview, setPreview] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setText(value || '');
      setPreview(false);
    }
  }, [isOpen, value]);

  if (!isOpen) return null;

  const handleSave = () => {
    onSave(text.trim());
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <FileText className="text-blue-600 dark:text-blue-400" size={22} />
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Model Business Rules
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">{modelName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X size={20} className="text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-4 space-y-3">
          {/* Markdown hint */}
          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <p className="text-sm text-blue-800 dark:text-blue-300 font-medium mb-1">
              Markdown Support
            </p>
            <p className="text-xs text-blue-700 dark:text-blue-400">
              **bold** | *italic* | `code` | # Heading | - List | &gt; Quote
            </p>
          </div>

          {/* Preview toggle */}
          <div className="flex justify-end">
            <button
              onClick={() => setPreview(p => !p)}
              className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
            >
              {preview ? '✏️ Edit' : '👁️ Preview'}
            </button>
          </div>

          {/* Editor / Preview */}
          {!preview ? (
            <textarea
              value={text}
              onChange={e => setText(e.target.value)}
              rows={16}
              placeholder={`Model-level business rules for ${modelName}...\n\nExample:\n- This model represents the commercial module\n- All monetary values are stored in BRL (centavos)\n- Soft-delete pattern: records are never physically removed\n- Audit columns (created_at, updated_at) are required on all tables`}
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          ) : (
            <div className="w-full min-h-64 p-4 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-900 overflow-auto prose prose-sm dark:prose-invert max-w-none">
              {text.trim() ? (
                <ReactMarkdown>{text}</ReactMarkdown>
              ) : (
                <p className="text-gray-400 italic">No rules defined</p>
              )}
            </div>
          )}

          {/* Char count */}
          <p className="text-xs text-gray-500 dark:text-gray-400 text-right">
            {text.length} characters
          </p>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Save size={16} />
            Save Rules
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModelBusinessRulesModal;
